/**
 * @typedef {Object} Model
 * @property {number} id
 * @property {string} name
 * @property {ModelSide[]} [availableSides]
 * @property {string} modelPath
 * @property {string} frontImagePath
 * @property {string} backImagePath
 * @property {string} leftImagePath
 * @property {string} rightImagePath
 * @property {Vector3} cameraPosition
 * @property {Vector3} cameraTarget
 * @property {Object.<ModelSide, Vector3>} [cameraPositionsBySide]
 * @property {Object.<ModelSide, Vector3>} [cameraTargetsBySide]
 * @property {Object.<ModelSide, Vector3>} [cameraUpBySide]
 * @property {Object.<ModelSide, Vector3>} [screenshotCameraPositions]
 * @property {number} realWorldWidthInches
 * @property {number} realWorldHeightInches
 * @property {number} realWorldDepthInches
 * @property {number} [realWorldTopWidthInches]
 */

import { Vector3 } from "three";

/**
 * @typedef {'front' | 'back' | 'left' | 'right' | 'top'} ModelSide
 */

export const MODEL_SIDES = ['front', 'back', 'left', 'right', 'top'];
export const FOUR_FACE_MODEL_SIDES = ['front', 'back', 'left', 'right'];

export const SIDE_ANGLES = {
  front: 0,
  back:  Math.PI,
  left:  -Math.PI / 2, 
  right: Math.PI / 2,
  top: 0,
};

export const getModelAvailableSides = (model) => model.availableSides ?? MODEL_SIDES;

const defaultValues = {
  cameraPosition: new Vector3(0, 0, 1.1),
  cameraTarget: new Vector3(0, 0, 0),
  fov: 50,
  availableSides: MODEL_SIDES,
  sideAngles: SIDE_ANGLES,
  screenshotFrame: {
    verticalScale: 1,
    yOffset: 0,
    perSide: {},
  },
  cameraPositionsBySide: {
    top: new Vector3(0, 1.1, 0),
  },
  cameraTargetsBySide: undefined,
  cameraUpBySide: {
    top: new Vector3(0, 0, -1),
  },
  screenshotCameraPositions: {
    top: new Vector3(0, 1.1, 0),
  },
  axisOfRotation: "y",
  realWorldWidthInches: 12,
  realWorldHeightInches: 12,
  realWorldDepthInches: 12,
};

const SCREENSHOT_FRAME_1 = {
  verticalScale: 0.35,
  yOffset: -0.18,
  perSide: {
    top: {
      verticalScale: 1,
      yOffset: 0,
    },
  },
};

const KEYCHAIN_CAMERA_POSITIONS = {
  front: new Vector3(0, 1, 0),
  back: new Vector3(0, -1, 0),
};

const KEYCHAIN_CAMERA_UP = {
  front: new Vector3(0, 0, -1),
  back: new Vector3(0, 0, 1),
};

const KEYCHAIN_SIDE_ANGLES = {
  front: Math.PI / 2,
  back: -Math.PI / 2,
};

const KEYCHAIN_DIMENSIONS = {
  realWorldWidthInches: 6.875,
  realWorldHeightInches: 3.0625,
  realWorldDepthInches: 1.25,
};

/**
 * Public model catalog.
 *
 * Side image paths are intentionally blank until the reference images exist.
 * If those images live in src/assets, import them here and assign the imported
 * URLs rather than hardcoding src/assets paths.
 * Set `availableSides` per model when a product supports fewer than all five
 * views, for example: ['front', 'back'].
 *
 * @type {Model[]}
 */
export const MODELS = [
  {
    id: 1,
    name: 'Large_Weekender_Brown',
    gltf: 'Large_Weekender_Brown.gltf',
    ...defaultValues,
    screenshotFrame: SCREENSHOT_FRAME_1,
    realWorldWidthInches: 22,
    realWorldHeightInches: 13.75,
    realWorldDepthInches: 11.8125,
  },
  {
    id: 2,
    name: 'Large_Weekender_Black',
    gltf: 'Large_Weekender_Black.gltf',
    ...defaultValues,
    screenshotFrame: SCREENSHOT_FRAME_1,
    realWorldWidthInches: 22,
    realWorldHeightInches: 13.75,
    realWorldDepthInches: 11.8125,
  },
  {
    id: 3,
    name: 'Small_Weekender_Brown',
    gltf: 'Small_weekender_Brown.gltf',
    ...defaultValues,
    screenshotFrame: SCREENSHOT_FRAME_1,
    realWorldWidthInches: 19.75,
    realWorldHeightInches: 11.125,
    realWorldDepthInches: 11.5,
  },
  {
    id: 4,
    name: 'Small_Weekender_Black',
    gltf: 'Small_weekender_Black.gltf',
    ...defaultValues,
    screenshotFrame: SCREENSHOT_FRAME_1,
    realWorldWidthInches: 19.75,
    realWorldHeightInches: 11.125,
    realWorldDepthInches: 11.5,
  },
  {
    id: 5,
    name: 'Backpack_Brown',
    gltf: 'Backpack_Brown.gltf',
    ...defaultValues,
    sideAngles: {
      front: Math.PI / 2,
      back:  -Math.PI / 2,
      left:  0, 
      right: Math.PI,
    },
    cameraPosition: new Vector3(0, 0.1, 1.25),
    cameraUpBySide: {
      top: new Vector3(-1, 0, 0),
    },
    realWorldWidthInches: 10.75,
    realWorldHeightInches: 16.875,
    realWorldDepthInches: 4.25,
  },
  {
    id: 6,
    name: 'Backpack_Black',
    gltf: 'Backpack_Black.gltf',
    ...defaultValues,
    sideAngles: {
      front: Math.PI / 2,
      back:  -Math.PI / 2,
      left:  0, 
      right: Math.PI,
    },
    cameraPosition: new Vector3(0, 0.1, 1.25),
    cameraUpBySide: {
      top: new Vector3(-1, 0, 0),
    },
    realWorldWidthInches: 10.75,
    realWorldHeightInches: 16.875,
    realWorldDepthInches: 4.25,
  },
   {
    id: 7,
    name: 'Keychain_Brown',
    gltf: 'Keychain_Brown.gltf',
    ...defaultValues,
    cameraPosition: KEYCHAIN_CAMERA_POSITIONS.front,
    availableSides: ['front', 'back'],
    cameraPositionsBySide: KEYCHAIN_CAMERA_POSITIONS,
    cameraUpBySide: KEYCHAIN_CAMERA_UP,
    screenshotCameraPositions: KEYCHAIN_CAMERA_POSITIONS,
    sideAngles: KEYCHAIN_SIDE_ANGLES,
    axisOfRotation: "x",
    ...KEYCHAIN_DIMENSIONS,
  },
  {
    id: 8,
    name: 'Keychain_Black',
    gltf: 'Keychain_Black.gltf',
    ...defaultValues,
    cameraPosition: KEYCHAIN_CAMERA_POSITIONS.front,
    availableSides: ['front', 'back'],
    cameraPositionsBySide: KEYCHAIN_CAMERA_POSITIONS,
    cameraUpBySide: KEYCHAIN_CAMERA_UP,
    screenshotCameraPositions: KEYCHAIN_CAMERA_POSITIONS,
    sideAngles: KEYCHAIN_SIDE_ANGLES,
    axisOfRotation: "x",
    ...KEYCHAIN_DIMENSIONS,
  },
  {
    id: 9,
    name: 'Toiletry_Brown',
    gltf: 'Toiletry_Brown.gltf',
    ...defaultValues,
    sideAngles: {
      front: Math.PI,
      back:  0,
      left:  Math.PI / 2, 
      right: -Math.PI / 2,
    },
    cameraUpBySide: {
      top: new Vector3(0, 0, 1),
    },
    realWorldWidthInches: 9,
    realWorldHeightInches: 4.625,
    realWorldDepthInches: 4.375,
  },
  {
    id: 10,
    name: 'Toiletry_Black',
    modelPath: '/models/Toiletry_Black/Toiletry_Black.gltf',
    gltf: 'Toiletry_Black.gltf',
    ...defaultValues,
    sideAngles: {
      front: Math.PI,
      back:  0,
      left:  Math.PI / 2, 
      right: -Math.PI / 2,
    },
    cameraUpBySide: {
      top: new Vector3(0, 0, 1),
    },
    realWorldWidthInches: 9,
    realWorldHeightInches: 4.625,
    realWorldDepthInches: 4.375,
  },
  {
    id: 11,
    name: 'Zippered_Brown',
    modelPath: '/models/Zippered_Brown/Zippered_Brown.gltf',
    gltf: 'Zippered_Brown.gltf',
    ...defaultValues,
    screenshotFrame: {
      verticalScale: 0.60,
      yOffset: -0.20,
      perSide: {
        top: {
          verticalScale: 1,
          yOffset: 0,
        },
      },
    },
    cameraPosition: new Vector3(0, 0, 1.25),
    realWorldWidthInches: 14.5, // bottom: 14.5, top: 18
    realWorldTopWidthInches: 18,
    realWorldHeightInches: 13.5625,
    realWorldDepthInches: 7.5,
  },
  {
    id: 12,
    name: 'Zippered_Black',
    modelPath: '/models/Zippered_Black/Zippered_Black.gltf',
    gltf: 'Zippered_Black.gltf',  
    ...defaultValues,
    screenshotFrame: {
      verticalScale: 0.60,
      yOffset: -0.20,
      perSide: {
        top: {
          verticalScale: 1,
          yOffset: 0,
        },
      },
    },
    cameraPosition: new Vector3(0, 0, 1.25),
    realWorldWidthInches: 14.5, // bottom: 14.5, top: 18
    realWorldTopWidthInches: 18,
    realWorldHeightInches: 13.5625,
    realWorldDepthInches: 7.5,
  },
];

export const MODELS_BY_ID = Object.fromEntries(
  MODELS.map((model) => [model.id, model])
);
